import type {DesignSelection, RefreshAccountResponse, SocialAccountConnectionResponse} from '@Components/social-media/social-media.types';
import {SocialAccountConnectionResult, SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {MaxPostersPerPlatform} from '@Components/social-media/account.vo';
import {getUserHashedId, getUserId, getUserPremiumLevel, isUserLoggedIn} from '@Libraries/user.library';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {isIOSApp, isMobileOrIOSApp} from '@Utils/browser.util';
import {openConfirmationModal} from '@Modals/confirmation-modal';
import {openErrorModal} from '@Modals/error-modal';
import type {VoidFunction} from '@/utils/general.util';
import {asyncnoop, noop} from '@/utils/general.util';
import {closeSocialAccountConnectionResponseModal, openSocialAccountConnectionResponseModal} from '@Modals/social-account-connection-response-modal';
import {dismissIOSSafariView, openLoginWindow} from '@Libraries/login-signup-library';

export const MAX_PAYG_PARENT_ACCOUNTS = 1;
export const MAX_PREMIUM_LEVEL_1_PARENT_ACCOUNTS = 3;
const OAUTH_POPUP_CLOSE_POLL_MS = 700;

export enum SocialMediaUpsellEvent {
  ACCOUNTS_PREMIUM_PLUS = 'SocialWizard_Upselling_Accounts_Premium',
  ACCOUNTS_PREMIUM = 'SocialWizard_Upselling_Accounts_Premium_Plus',
  SCHEDULE = 'SocialWizard_Upselling_Schedule',
}

export const getSocialAccountsUpsellByPremiumLevel = (): SocialMediaUpsellEvent => {
  if (window.PMW.getUserPremiumLevel() === USER_PREMIUM_LEVELS.PREMIUM) {
    return SocialMediaUpsellEvent.ACCOUNTS_PREMIUM_PLUS;
  }

  return SocialMediaUpsellEvent.ACCOUNTS_PREMIUM;
};

export const doesAccountHaveCarouselPosts = (accountType: SocialPlatform | undefined): boolean => {
  switch (accountType) {
    case SocialPlatform.INSTAGRAM:
    case SocialPlatform.PINTEREST:
    case SocialPlatform.THREADS:
      return true;
    default:
      return false;
  }
};

export const onConnectAccount = (accountType: SocialPlatform): void => {
  switch (accountType) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_GROUP:
      openOAuthPopup('socialmedia/facebookOAuth');
      break;
    case SocialPlatform.INSTAGRAM:
      openOAuthPopup('socialmedia/instagramBusinessOAuth');
      break;
    case SocialPlatform.YOUTUBE:
      onConnectYouTube();
      break;
    case SocialPlatform.TWITTER:
      openOAuthPopup('socialmedia/twitterOAuth');
      break;
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      openOAuthPopup('socialmedia/linkedInOAuth');
      break;
    case SocialPlatform.TIKTOK:
      openOAuthPopup('socialmedia/tiktokOAuth');
      break;
    case SocialPlatform.PINTEREST:
      openOAuthPopup('socialmedia/pinterestOAuth');
      break;
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      openOAuthPopup('socialmedia/googleBusinessProfileOAuth');
      break;
    case SocialPlatform.THREADS:
      openOAuthPopup('socialmedia/threadsOAuth');
      break;
    default:
      console.error('Illegal account type');
  }
};

export const getAuthUriForAccountType = (accountType: SocialPlatform): string => {
  switch (accountType) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_GROUP:
      return 'socialmedia/getFacebookOAuthUrl';
    case SocialPlatform.INSTAGRAM:
      return 'socialmedia/getInstagramBusinessOauthUrl';
    case SocialPlatform.YOUTUBE:
      return 'socialmedia/getYoutubeOAuthUrl';
    case SocialPlatform.TWITTER:
      return 'socialmedia/getTwitterOAuthUrl';
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      return 'socialmedia/getLinkedInOAuthUrl';
    case SocialPlatform.TIKTOK:
      return 'socialmedia/getTiktokOAuthUrl';
    case SocialPlatform.PINTEREST:
      return 'socialmedia/getPinterestOAuthUrl';
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return 'socialmedia/getGoogleBusinessProfileOAuthUrl';
    case SocialPlatform.THREADS:
      return 'socialmedia/getThreadsOAuthUrl';
    default:
      throw new Error(`Illegal account type${accountType}`);
  }
};

const getAuthUrl = async (accountType: SocialPlatform): Promise<string> => {
  return (await window.PMW.readLocal(getAuthUriForAccountType(accountType), {
    isIOSApp: true,
  })) as string;
};

const initAccountConnection = async (accountType: SocialPlatform, onAccountCreated: () => void): Promise<void> => {
  await openLoginWindow(getAuthUrl.bind(null, accountType), noop);
  const ACCOUNT_CONNECTION_TIMEOUT_MS = 300000;
  const connectionData = (await window.PMW.pollingAjaxCallAsync(
    asyncnoop,
    () => {
      return window.PMW.readLocal('socialmedia/hasAccountBeenAdded', {
        platform: accountType,
      });
    },
    ACCOUNT_CONNECTION_TIMEOUT_MS
  )) as SocialAccountConnectionResponse | null;

  const validatedConnectionData = validateAccountAddedResponse(connectionData);
  handleAccountConnectionResponse(validatedConnectionData, onAccountCreated);
};

const validateAccountAddedResponse = (response: SocialAccountConnectionResponse | null | string): SocialAccountConnectionResponse | null => {
  if (typeof response === 'string') {
    return JSON.parse(response) as SocialAccountConnectionResponse;
  }

  return response;
};

export const startAccountConnection = (accountType: SocialPlatform, onAccountCreated: () => void): void => {
  if (isMobileOrIOSApp()) {
    void onConnectAccountForIosApp(accountType, onAccountCreated);
    return;
  }

  onConnectAccount(accountType);
};

export const onConnectAccountForIosApp = async (accountType: SocialPlatform, onAccountCreated: () => void): Promise<void> => {
  if (accountType === SocialPlatform.YOUTUBE) {
    onConnectYouTube(initAccountConnection.bind(null, accountType, onAccountCreated));
  } else {
    await initAccountConnection(accountType, onAccountCreated);
  }
};

const onConnectYouTube = (initiateConnection?: () => Promise<void>): void => {
  openConfirmationModal({
    onConfirm: (): void => {
      if (initiateConnection) {
        void initiateConnection();
      } else {
        openOAuthPopup('socialmedia/youtubeOAuth');
      }
    },
    icon: 'icon-check',
    infoText: window.i18next.t('pmwjs_youtube_agreement_point', {
      ytCommunityGuidelines: 'https://www.youtube.com/howyoutubeworks/policies/community-guidelines/',
      googlePrivacyPolicy: 'http://www.google.com/policies/privacy',
      ytTermsOfService: 'https://www.youtube.com/t/terms',
      privacyPolicy: 'https://www.postermywall.com/index.php/info/termsofuse',
      googleSecuritySettings: 'https://security.google.com/settings/security/permissions',
    }),
    doShowContentIcon: false,
    confirmButtonText: window.i18next.t('pmwjs_agree_to_all'),
    panelTitle: window.i18next.t('pmwjs_add_youtube_channel'),
  });
};

export const isTwitterCurrentlyUnusable = (account: SocialAccount): boolean => {
  if (account.type === SocialPlatform.TWITTER) {
    // TODO: switch to false when we're back up
    return false;
  }

  return false;
};

export const isPostersCountValidForMultiPosting = (posters: DesignSelection[], account: SocialAccount): boolean => {
  const accountPlatform = account.type;
  const maxNumberOfPosts = MaxPostersPerPlatform[accountPlatform];

  if (maxNumberOfPosts === null) {
    return true;
  }

  return posters.length <= maxNumberOfPosts;
};

export const isYouTubeCurrentlyUnusable = (account: SocialAccount): boolean => {
  if (account.type === SocialPlatform.YOUTUBE) {
    // TODO: switch to false when we're back up
    return false;
  }

  return false;
};

export const isGoogleBusinessProfileAccountUnverified = (account: SocialAccount): boolean => {
  if (account.type === SocialPlatform.GOOGLE_BUSINESS_PROFILE) {
    return !account.isVerifiedGoogleBusinessProfileAccount;
  }

  return false;
};

const openOAuthPopup = (oAuthURI: string): void => {
  window.open(window.PMW.util.site_url(oAuthURI), '_blank');
};

export const sortPlanLockedAccountsLast = (accounts: SocialAccount[]): SocialAccount[] => {
  if (accounts.length <= 1) {
    return accounts;
  }

  return [...accounts].sort((a, b) => Number(a.isLockedByPlanLimit) - Number(b.isLockedByPlanLimit));
};

export const getNumParentAccounts = (accounts: SocialAccount[]): number => {
  let numAccounts = 0;
  accounts.forEach((account) => {
    if (account.type !== SocialPlatform.FACEBOOK_PAGE && account.type !== SocialPlatform.FACEBOOK_GROUP) {
      numAccounts += 1;
    }
  });

  return numAccounts;
};

export const isEligibleForSocialAccountsUpsellDialog = (numAccounts: number): boolean => {
  const premiumLevel = getUserPremiumLevel();

  if (premiumLevel === USER_PREMIUM_LEVELS.PREMIUM && numAccounts >= MAX_PREMIUM_LEVEL_1_PARENT_ACCOUNTS) {
    return true;
  }

  if (premiumLevel === USER_PREMIUM_LEVELS.PAYG && numAccounts >= MAX_PAYG_PARENT_ACCOUNTS) {
    return true;
  }

  return false;
};

const onPremiumUpsellClicked = (eventToTrack = ''): void => {
  if (eventToTrack) {
    window.PMW.gtm.trackGA4CustomEvent(eventToTrack);
  }
};

const getUpsellDialogNameByPremiumLevel = (userPremiumLevel: USER_PREMIUM_LEVELS): string => {
  if (userPremiumLevel === USER_PREMIUM_LEVELS.PAYG) {
    return window.PMW.PREMIUM_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS as string;
  }

  return window.PMW.PREMIUM_PLUS_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS as string;
};

export const showSocialMediaPublishingUpsellDialog = (eventToTrack = '', userPremiumLevel: number = USER_PREMIUM_LEVELS.PAYG): void => {
  const upsellDialogName = getUpsellDialogNameByPremiumLevel(userPremiumLevel);
  const eventName = window.PMW.getPremiumUpsellClickEvent(upsellDialogName) as string;
  const eventHandler = (): void => {
    onPremiumUpsellClicked(eventToTrack);
  };

  void window.PMW.showPremiumOnlyFeatureDialog(upsellDialogName);
  window.addEventListener(eventName, eventHandler, {
    once: true,
  });
};

export const openSocialAccountsUpsellDialog = (): void => {
  showSocialMediaPublishingUpsellDialog(getSocialAccountsUpsellByPremiumLevel(), getUserPremiumLevel());
};

export const shareSocialMediaAccountInTeam = async (isSharedInTeam: boolean, idAccount: number): Promise<void> => {
  if (isUserLoggedIn()) {
    await window.PMW.writeLocal('socialmedia/updateSocialMediaAccountSharingStatus', {
      shareInTeam: isSharedInTeam,
      idAccount: idAccount,
    });
  }
};

const reAuthenticateForIOS = async (idAccount: number, accountType: SocialPlatform): Promise<void> => {
  try {
    const response = (await window.PMW.readLocal('socialmedia/refreshAccountForIOSApp', {
      idAccount,
      platform: accountType,
    })) as RefreshAccountResponse;
    if (response.url) {
      window.open(response.url, '_blank');
    }
  } catch (e) {
    openErrorModal();
  }
};

export const reAuthenticateInModal = (idAccount: number, accountType: SocialPlatform): Window | null => {
  if (isIOSApp()) {
    void reAuthenticateForIOS(idAccount, accountType);
    return null;
  }

  return window.open(window.PMW.util.site_url(`socialmedia/refreshAccount?idAccount=${idAccount}&platform=${accountType}`), '', 'width=600,height=600,top=0,=right=50%,=scrollbars=yes');
};

export const isAccountInSearch = (searchTerm: string | undefined, accountName: string): boolean => {
  if (!searchTerm || searchTerm === '') {
    return true;
  }

  // Convert both the account name and searchTerm to lowercase for case-insensitive matching
  const lowerCaseAccountName = accountName.toLowerCase();
  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  return lowerCaseAccountName.includes(lowerCaseSearchTerm);
};

export const shouldShowShareInTeamForAccount = (account: SocialAccount): boolean => {
  return account.idUser === getUserId();
};

export const isSocialMediaAccountSharedByTeamMember = (account: SocialAccount): boolean => {
  return account.isSharedInTeam && account.idUser !== getUserId();
};

export const addConnectAccountBroadcastChannel = (onSuccessMessageReceived: (eventData: MessageEvent) => void): BroadcastChannel | undefined => {
  const accountCreatedBroadcastName = getConnectAccountBroadcastName();

  if (!accountCreatedBroadcastName) {
    return;
  }

  const accountCreatedBroadcastChannel = new BroadcastChannel(accountCreatedBroadcastName);
  accountCreatedBroadcastChannel.addEventListener('message', onSuccessMessageReceived);

  return accountCreatedBroadcastChannel;
};

export const removeConnectAccountBroadcastChannel = (accountCreatedBroadcastChannel?: BroadcastChannel): void => {
  if (!accountCreatedBroadcastChannel) {
    return;
  }

  accountCreatedBroadcastChannel.close();
};

export const getConnectAccountBroadcastName = (): string => {
  const hashedUserId = getUserHashedId();
  if (hashedUserId) {
    return hashedUserId;
  }

  return '';
};

export const getAccountConnectionResponseFromBroadcast = (broadcastEvent: MessageEvent): SocialAccountConnectionResponse | null => {
  const connectionResponse: SocialAccountConnectionResponse = broadcastEvent.data as SocialAccountConnectionResponse;

  if (!connectionResponse) {
    return null;
  }

  return connectionResponse;
};

export const handleAccountConnectionResponse = (
  connectionResponse: SocialAccountConnectionResponse | null,
  onSuccess: VoidFunction = noop,
  onNothingHappened: VoidFunction = noop,
  onFailure: VoidFunction = noop
): void => {
  dismissIOSSafariView();
  if (!connectionResponse) {
    return;
  }

  if (connectionResponse.hideExistingModal) {
    closeSocialAccountConnectionResponseModal();
  }

  if (connectionResponse.socialAccountConnectionResult === SocialAccountConnectionResult.NOTHING_HAPPENED) {
    onNothingHappened();
    return;
  }

  if (connectionResponse.socialAccountConnectionResult === SocialAccountConnectionResult.ACCOUNT_ADDED_SUCCESS) {
    onSuccess();
    return;
  }

  onFailure();
  openSocialAccountConnectionResponseModal({connectionResponse: connectionResponse});
};

export interface ReconnectWithBroadcastOpts {
  idAccount: number;
  accountType: SocialPlatform;
  previousChannel: BroadcastChannel | undefined;
  onSuccess: VoidFunction;
  onNothingHappened: VoidFunction;
  onFailure: VoidFunction;
}

/**
 * Single reconnect handshake: closes any previously-opened broadcast channel (so repeated
 * reconnects on the same account never leak a listener), opens a fresh one wired to the
 * connection-response callbacks, then triggers the reAuthenticateInModal OAuth popup.
 * Returns the new channel so the caller can hold it for the next call and for unmount cleanup.
 */
export const runReconnectWithBroadcast = (opts: ReconnectWithBroadcastOpts): BroadcastChannel | undefined => {
  const {idAccount, accountType, previousChannel, onSuccess, onNothingHappened, onFailure} = opts;

  removeConnectAccountBroadcastChannel(previousChannel);

  let isResolved = false;
  let popupClosePollId: ReturnType<typeof setInterval> | undefined;

  // The first of {broadcast result, popup closed} wins; a genuine success/failure broadcast always fires before the popup-closed poll ticks, so closing the window is only treated as abandonment when no result arrived.
  const resolveOnce = (handler: VoidFunction): void => {
    if (isResolved) {
      return;
    }
    isResolved = true;
    if (popupClosePollId !== undefined) {
      clearInterval(popupClosePollId);
    }
    handler();
  };

  const onMessage = (event: MessageEvent): void => {
    const connectionResponse = getAccountConnectionResponseFromBroadcast(event);
    handleAccountConnectionResponse(
      connectionResponse,
      () => {
        resolveOnce(onSuccess);
      },
      () => {
        resolveOnce(onNothingHappened);
      },
      () => {
        resolveOnce(onFailure);
      }
    );
  };

  const channel = addConnectAccountBroadcastChannel(onMessage);
  const oAuthPopup = reAuthenticateInModal(idAccount, accountType);

  if (oAuthPopup) {
    popupClosePollId = setInterval((): void => {
      if (oAuthPopup.closed) {
        resolveOnce(onNothingHappened);
      }
    }, OAUTH_POPUP_CLOSE_POLL_MS);
  }

  return channel;
};
